import React, { useCallback, useState } from 'react';
import { withAppBasename } from '../../../config/appBase';

/** What each refusal code from the interview routes (app/services/
 * interview_access.py's package_refusal: {error, code, upgrade_required})
 * says to a student. The code itself is never shown. `upgrade` is whether
 * buying something changes the answer: a used free mock has no upgrade path,
 * and neither does a tier refusal for someone who already holds a paid
 * exam (purchases are not upgraded). */
const GATE_COPY = {
  package_required: {
    title: 'Interview practice needs an interview package',
    body: 'Choose an interview package to unlock live interviews and mock interview papers.',
    upgrade: true,
  },
  subject_required: {
    title: 'This course is for a different subject',
    body: 'Your Oxbridge package covers a different subject, so this course is not part of it.',
    upgrade: true,
  },
  tier_required: {
    title: 'This interview is not in your package',
    body: 'Your package tier does not include this interview.',
    upgrade: true,
    upgradeUnlessPaid: true,
  },
  live_mock_used: {
    title: 'Your free live interview has been used',
    body: 'The free package includes one live mock interview, and it has already been taken. You can still read its feedback.',
    upgrade: false,
  },
};

const GENERIC_COPY = {
  title: 'Interview practice is on Beyond',
  body: 'Upgrade your plan for live interviews and marked mock interview papers.',
  upgrade: true,
};

/** The refusal code carried on a 403 from the interview routes, or ''. */
export function refusalCode(err) {
  const code = err?.data?.code;
  return typeof code === 'string' ? code : '';
}

/** The copy for a code. A missing or unknown code keeps the generic copy. */
export function gateCopy(code, paid = false) {
  const copy = Object.prototype.hasOwnProperty.call(GATE_COPY, code) ? GATE_COPY[code] : GENERIC_COPY;
  const showLink = copy.upgrade && !(copy.upgradeUnlessPaid && paid);
  return { title: copy.title, body: copy.body, showLink };
}

/** One plain sentence for a refusal met mid-page (an answer, a turn), where
 * the page cannot be replaced by the gate: the code's own sentence, else
 * `fallback` for an uncoded refusal. */
export function refusalSentence(err, fallback) {
  const code = refusalCode(err);
  return Object.prototype.hasOwnProperty.call(GATE_COPY, code) ? GATE_COPY[code].body : fallback;
}

/** The one 403 shape every /api/interviews route shares. Shown identically
 * wherever a browse screen or the runtime hits it, rather than re-writing this
 * copy at each call site. `code` picks the sentence; `paid` is whether the
 * account holds a paid exam. */
export default function InterviewUpgradeGate({ code = '', paid = false }) {
  const { title, body, showLink } = gateCopy(code, paid);
  return (
    <div className="ib__upgrade">
      <h2>{title}</h2>
      <p>{body}</p>
      {showLink && <a href={withAppBasename('/pricing')}>See plans</a>}
    </div>
  );
}

/** True for the exact shape the interview routes return on 403. Anything
 * else (a 503 read failure, a network error) is a different, real error
 * and must not be silently shown as "upgrade your plan". */
export function isUpgradeRequiredError(err) {
  return err?.status === 403 && err?.data?.upgrade_required === true;
}

/** For a page whose load can be refused: `statusFor(err)` returns the page
 * status for a failed call ('upgrade' for the refusal shape, else 'error')
 * and remembers the refusal's code, which the page hands to the gate. */
export function useGateStatus() {
  const [gateCode, setGateCode] = useState('');
  const statusFor = useCallback((err) => {
    if (!isUpgradeRequiredError(err)) return 'error';
    setGateCode(refusalCode(err));
    return 'upgrade';
  }, []);
  return [gateCode, statusFor];
}
